import React from "react";
import { HelpCircle, Phone, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Button } from "./ui/button";
import { useQuoteModal } from "@/hooks/useQuoteModal";

const faqs = [
  {
    question: "Do you offer junk removal services in Davie, FL?",
    answer: "Yes! Muscle Junk Removal is headquartered in Davie, FL and provides full-service junk removal across all Davie zip codes (33314, 33324, 33328, 33330, 33331) including Pine Island Ridge, Shenandoah, Forest Ridge, and Hawkes Bluff. Same-day and next-day appointments are available."
  },
  {
    question: "Do you provide junk removal in Fort Lauderdale, Hollywood, and Plantation?",
    answer: "Yes! We offer junk removal services throughout Fort Lauderdale, Hollywood, Plantation, Cooper City, Sunrise, and all of Broward County. Our trucks are dispatched locally daily and we cover a 30-mile service radius from Davie."
  },
  {
    question: "What are your junk removal prices?",
    answer: "Our junk removal prices are based on volume, starting at $99 for a single item, $199-$299 for a 1/4 truckload, $349-$449 for a 1/2 truckload, and $649-$799 for a full truckload. All pricing is upfront with no hidden fees. Use our online price estimator for an instant ballpark quote."
  },
  {
    question: "Do you serve Cooper City and Sunrise for junk removal?",
    answer: "Absolutely. We provide junk removal services to Cooper City, Sunrise, and all surrounding Broward County cities. Being locally based in Davie means we can reach these areas quickly for same-day junk hauling."
  },
  {
    question: "What junk removal services do you offer?",
    answer: "We offer residential and commercial junk removal services including furniture removal, appliance pickup and recycling, garage and attic cleanouts, storage unit cleanouts, construction debris removal, yard waste hauling, e-waste recycling, estate and foreclosure cleanouts, eviction property cleanouts, and hoarding cleanup across Davie and Broward County."
  },
  {
    question: "How fast can you come out to my home in Davie?",
    answer: "We offer same-day and next-day junk removal services throughout Davie (33314, 33324, 33328, 33330, 33331) and Broward County. When you submit a quote request or call (954) 280-8090, we typically arrive within hours."
  },
  {
    question: "Do I need to be home for the pickup?",
    answer: "Not necessarily! As long as the items are accessible (outside in a driveway, yard, carport, or open garage) and we have phone confirmation for approval, our crew can perform the removal and process payment digitally."
  },
  {
    question: "What happens to my junk after you haul it away?",
    answer: "We are committed to eco-friendly disposal in South Florida. We sort every truckload. Usable furniture and clothing are donated to local Broward charities; scrap metal and electronics go to certified recycling centers, keeping landfill waste to an absolute minimum."
  },
  {
    question: "Are you licensed and insured for commercial property cleanouts?",
    answer: "Yes! Muscle Junk Removal is fully licensed and carries comprehensive commercial general liability insurance to protect your residential or commercial property."
  }
];

export const FAQSection: React.FC = () => {
  const { openModal } = useQuoteModal();
  return (
    <section id="faq" className="py-20 bg-slate-50 relative">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-slate-900 font-extrabold px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4">
            <HelpCircle className="w-4 h-4 text-primary" /> Davie FL FAQs
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading text-slate-950 uppercase tracking-tight mb-4">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>
          <p className="text-slate-600 text-base sm:text-lg">
            Got questions about junk hauling in Davie, FL? We’ve got answers!
          </p>
        </div>

        {/* Accordion list */}
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xl mb-12">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-slate-200 rounded-xl px-4 py-1">
                <AccordionTrigger className="text-left font-black text-slate-950 text-base sm:text-lg hover:text-primary hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm sm:text-base leading-relaxed pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CTA Box under FAQ */}
        <div className="bg-slate-950 text-white rounded-3xl p-8 text-center border-2 border-amber-400/40">
          <h3 className="text-2xl font-black font-heading uppercase text-white mb-2">
            Still Have Questions For Our Davie Crew?
          </h3>
          <p className="text-slate-300 text-sm mb-6 max-w-md mx-auto">
            Give us a call right now or send a quick message for instant help!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:9542808090">
              <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-rose-700 text-white font-extrabold uppercase">
                <Phone className="w-4 h-4 mr-2" /> Call (954) 280-8090
              </Button>
            </a>
            <Button size="lg" onClick={openModal} className="w-full sm:w-auto bg-amber-400 hover:bg-amber-500 text-slate-950 font-extrabold uppercase">
                Get Free Quote <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
          </div>
        </div>

      </div>
    </section>
  );
};
